import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface ProductGroupSession {
  id: string;
  productGroupId: string;
  groupId: string;
  groupName: string;
  lineId: string;
  lineName: string;
  shiftNumber: number;
  productionDate: string;
  startTime: string;
  endTime?: string;
  productionTimeSeconds: number;
  prepareTimeSeconds: number;
  cleanTimeSeconds: number;
  stopTimeSeconds: number;
  isActive: boolean;
  currentMode: string;
  lastModeChange?: string;
}

interface ProductGroupContextType {
  activeProductGroups: Record<string, ProductGroupSession>;
  selectedLineGroup: ProductGroupSession | null;
  isLoading: boolean;
  error: string | null;
  refreshProductGroups: (shift: number) => Promise<void>;
  switchProductGroup: (lineId: string, productGroupId: string, shift: number) => Promise<boolean>;
  setSelectedLine: (lineId: string | null) => void;
}

const ProductGroupContext = createContext<ProductGroupContextType | undefined>(undefined);

export const useProductGroupContext = () => {
  const context = useContext(ProductGroupContext);
  if (!context) {
    throw new Error('useProductGroupContext must be used within ProductGroupProvider');
  }
  return context;
};

interface ProductGroupProviderProps {
  children: React.ReactNode;
}

export const ProductGroupProvider: React.FC<ProductGroupProviderProps> = ({ children }) => {
  const [activeProductGroups, setActiveProductGroups] = useState<Record<string, ProductGroupSession>>({});
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number>(0);

  const protocol = window.location.protocol;
  const hostname = window.location.hostname || 'localhost';
  const port = window.location.port ? `:${window.location.port}` : '';
  const apiUrl = `${protocol}//${hostname}${port}/api`;

  const refreshProductGroups = useCallback(async (shift: number) => {
    // Prevent rapid successive fetches (debounce)
    const now = Date.now();
    if (now - lastFetch < 500) {
      console.log('Skipping fetch - too soon after last fetch');
      return;
    }
    setLastFetch(now);

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch(`${apiUrl}/product-group-sessions/active-all?shift=${shift}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.message === 'success' && Array.isArray(result.data)) {
        const groupsByLine: Record<string, ProductGroupSession> = {};
        
        result.data.forEach((session: any) => {
          if (session.lineId) {
            // Normalize the session data
            groupsByLine[session.lineId] = {
              ...session,
              groupName: session.groupName || session.productGroupName,
              productionTimeSeconds: session.productionTimeSeconds || 0,
            };
          }
        });
        
        setActiveProductGroups(groupsByLine);
        console.log('ProductGroupContext: Updated groups for', Object.keys(groupsByLine).length, 'lines');
      } else {
        console.warn('Unexpected response format:', result);
        setActiveProductGroups({});
      }
    } catch (err) {
      console.error('ProductGroupContext: Error fetching groups:', err);
      setError(err.message || 'Failed to fetch product groups');
      // Don't clear existing data on error - keep last known state
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, lastFetch]);

  const switchProductGroup = useCallback(async (
    lineId: string, 
    productGroupId: string, 
    shift: number
  ): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch(`${apiUrl}/product-group-sessions/switch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productGroupId,
          shiftNumber: shift,
          lineId,
          currentMode: activeProductGroups[lineId]?.currentMode || 'stop'
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || errorData.details || `Server error: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.data) {
        // Update local state immediately
        setActiveProductGroups(prev => ({
          ...prev,
          [lineId]: result.data
        }));
        
        // Refresh all groups after a short delay
        setTimeout(() => refreshProductGroups(shift), 500);
        
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('ProductGroupContext: Error switching group:', err);
      setError(err.message || 'Failed to switch product group');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, activeProductGroups, refreshProductGroups]);

  const setSelectedLine = useCallback((lineId: string | null) => {
    setSelectedLineId(lineId);
  }, []);

  // Compute selected line's group
  const selectedLineGroup = selectedLineId && activeProductGroups[selectedLineId] 
    ? activeProductGroups[selectedLineId] 
    : null;

  const value = {
    activeProductGroups,
    selectedLineGroup,
    isLoading,
    error,
    refreshProductGroups,
    switchProductGroup,
    setSelectedLine,
  };

  return (
    <ProductGroupContext.Provider value={value}>
      {children}
    </ProductGroupContext.Provider>
  );
};